import { useMemo, useState } from "react";
import { Bell, Check, ExternalLink, History, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useTrendNotifySettings } from "@/hooks/useTrendNotifySettings";
import {
  formatDataAge,
  trendDeepLinkPath,
  type TrendNotifyHistoryEntry,
} from "@/lib/trendNotifications";

const VISIBLE_STEP = 8;

/**
 * Reviewable log of past new-trend announcements: what was announced, whether
 * it was read, and whether it was dismissed. Each entry deep-links to the
 * trend details modal.
 */
export function NotificationHistoryPanel() {
  const { settings, historyUnreadCount, markHistoryRead, dismissHistory, clearHistory, syncing } =
    useTrendNotifySettings();
  const [showDismissed, setShowDismissed] = useState(false);
  const [visible, setVisible] = useState(VISIBLE_STEP);

  const entries = useMemo<TrendNotifyHistoryEntry[]>(
    () =>
      (showDismissed
        ? settings.history
        : settings.history.filter((e) => !e.dismissed)
      ).slice(0, visible),
    [settings.history, showDismissed, visible],
  );
  const totalShown = showDismissed
    ? settings.history.length
    : settings.history.filter((e) => !e.dismissed).length;

  return (
    <section
      className="rounded-xl border border-border bg-card p-5"
      data-testid="trend-notification-history"
      aria-labelledby="trend-history-title"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="trend-history-title"
          className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground"
        >
          <History className="size-3.5" aria-hidden="true" />
          NOTIFICATION HISTORY
        </h2>
        <div className="flex items-center gap-2">
          {syncing && (
            <span
              data-testid="trend-history-syncing"
              className="font-mono text-[11px] text-muted-foreground"
              role="status"
            >
              syncing…
            </span>
          )}
          <span
            data-testid="trend-history-unread"
            className={`rounded-full px-2 py-0.5 font-mono text-[11px] ${
              historyUnreadCount > 0
                ? "bg-primary/15 text-primary"
                : "bg-secondary text-muted-foreground"
            }`}
            aria-live="polite"
          >
            {historyUnreadCount} unread
          </span>
          <button
            type="button"
            data-testid="trend-history-toggle-dismissed"
            onClick={() => setShowDismissed((v) => !v)}
            className="min-h-9 rounded-lg border border-border px-2.5 text-xs transition hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-pressed={showDismissed}
          >
            {showDismissed ? "Hide dismissed" : "Show dismissed"}
          </button>
          <button
            type="button"
            data-testid="trend-history-clear"
            onClick={clearHistory}
            disabled={settings.history.length === 0}
            className="flex min-h-9 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs transition hover:bg-secondary/50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            Clear
          </button>
        </div>
      </div>

      {settings.history.length === 0 ? (
        <p
          className="mt-3 text-sm text-muted-foreground"
          data-testid="trend-history-empty"
        >
          No trend notifications yet. New curated trends will show up here as soon as they
          land.
        </p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="trend-history-list">
          {entries.map((entry) => (
            <li
              key={entry.id}
              data-testid="trend-history-item"
              data-read={entry.read ? "true" : "false"}
              data-dismissed={entry.dismissed ? "true" : "false"}
              className={`rounded-lg border p-3 ${
                entry.dismissed
                  ? "border-border/60 bg-secondary/30 opacity-70"
                  : entry.read
                    ? "border-border"
                    : "border-primary/40 bg-primary/5"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-medium">
                    <Bell className="size-3.5 shrink-0 text-primary" aria-hidden="true" />
                    <span className="truncate">{entry.title}</span>
                  </p>
                  <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                    {entry.kind === "summary" ? "DAILY SUMMARY" : "INSTANT"} ·{" "}
                    {formatDataAge(entry.createdAt)} · {entry.trendIds.length} trend
                    {entry.trendIds.length === 1 ? "" : "s"} ·{" "}
                    {entry.dismissed ? "dismissed" : entry.read ? "read" : "unread"}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Link
                    to={trendDeepLinkPath(entry.trendIds[0])}
                    data-testid="trend-history-open"
                    onClick={() => markHistoryRead(entry.id)}
                    className="flex min-h-9 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs transition hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <ExternalLink className="size-3.5" aria-hidden="true" />
                    View trend
                  </Link>
                  <button
                    type="button"
                    data-testid="trend-history-mark-read"
                    onClick={() => markHistoryRead(entry.id)}
                    disabled={entry.read}
                    aria-label={`Mark "${entry.title}" as read`}
                    className="flex size-9 items-center justify-center rounded-lg border border-border transition hover:bg-secondary/50 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <Check className="size-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    data-testid="trend-history-dismiss"
                    onClick={() => dismissHistory(entry.id)}
                    disabled={entry.dismissed}
                    aria-label={`Dismiss "${entry.title}"`}
                    className="flex size-9 items-center justify-center rounded-lg border border-border transition hover:bg-secondary/50 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {totalShown > visible && (
        <button
          type="button"
          data-testid="trend-history-more"
          onClick={() => setVisible((v) => v + VISIBLE_STEP)}
          className="mt-3 min-h-9 rounded-lg border border-border px-3 text-xs transition hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Show older notifications
        </button>
      )}
    </section>
  );
}
